import type React from 'react';
import { useState, useTransition } from 'react';
import { useInView } from 'react-intersection-observer';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@xing-com/button';
import { SEARCH_PAGE_SIZE } from '@xing-com/crate-jobs-constants';
import { useSearchTracking } from '@xing-com/crate-jobs-tracking';
import { DotLoadingIndicator } from '@xing-com/dot-loading-indicator';
import { mediaTopBarVisible, mediaTopBarHidden } from '@xing-com/layout-tokens';
import { space3XL, spaceL } from '@xing-com/tokens';

import type { useSearch } from '../../../hooks';
import { NewJobsSeenBanner } from '../new-jobs-seen-banner';

type LazyLoaderProps = {
  isSerps?: boolean;
  hasSinceFilter?: boolean;
  fetchMore: ReturnType<typeof useSearch>['fetchMore'];
  results: ReturnType<typeof useSearch>['results'];
};

export const LazyLoader: React.FC<LazyLoaderProps> = ({
  isSerps = false,
  hasSinceFilter,
  fetchMore,
  results,
}) => {
  const [isPending, startTransition] = useTransition();
  const { trackFetchMore } = useSearchTracking();
  const [page, setPage] = useState(() => {
    const listLength = results?.collection.length ?? SEARCH_PAGE_SIZE;
    return Math.ceil(listLength / SEARCH_PAGE_SIZE);
  });

  const { ref } = useInView({
    onChange: (visible) => {
      if (visible) {
        fetchMoreResults();
      }
    },
  });

  const totalResults = Infinity;
  const offset = SEARCH_PAGE_SIZE * page;
  const aiNoMoreResults = (results?.collection.length ?? 0) < offset;

  if (totalResults <= offset || aiNoMoreResults) {
    return hasSinceFilter ? <NewJobsSeenBanner /> : null;
  }

  const fetchMoreResults = async (): Promise<void> => {
    startTransition(() => {
      (async () => {
        const fetchMoreData = await fetchMore({
          variables: {
            offset,
            returnAggregations: false,
          },
          // Can't use a type policy (https://www.apollographql.com/docs/react/pagination/core-api/#merging-paginated-results)
          // because of this bug: https://github.com/apollographql/apollo-client/issues/10711
          // @ts-expect-error generated and fetchMore types are incompatible because of undefined cases
          updateQuery: (previousResult, { fetchMoreResult }) => ({
            ...previousResult,
            jobSearchByQuery: {
              ...(previousResult?.jobSearchByQuery ?? {}),
              collection: [
                ...(previousResult?.jobSearchByQuery?.collection ?? []),
                ...(fetchMoreResult?.jobSearchByQuery?.collection ?? []),
              ],
            },
          }),
        });

        if (!fetchMoreData?.data?.jobSearchByQuery || isPending) {
          return;
        }

        trackFetchMore({
          page: page + 1,
          results: fetchMoreData.data.jobSearchByQuery,
        });
      })();
      setPage(page + 1);
    });
  };

  const SmallScreenComponents = isPending ? (
    <SmallScreenLoadingIndicator />
  ) : (
    <SmallScreenLoader ref={ref} />
  );

  const ContentContainerComponent = isSerps
    ? ContentContainer
    : ConfinedContentContainer;

  return (
    <Container>
      {!isSerps ? SmallScreenComponents : null}
      <ContentContainerComponent>
        <Button
          onClick={fetchMoreResults}
          variant="secondary"
          loading={isPending}
        >
          <FormattedMessage id="JOBS_SEARCH_PAGINATION_LOAD_MORE" />
        </Button>
      </ContentContainerComponent>
    </Container>
  );
};

const Container = styled.div`
  margin-bottom: ${space3XL};
`;

const SmallScreenLoadingIndicator = styled(DotLoadingIndicator)`
  justify-content: center;

  @media ${mediaTopBarHidden} {
    display: none;
  }
`;

const SmallScreenLoader = styled.div`
  @media ${mediaTopBarHidden} {
    display: none;
  }
`;

const ContentContainer = styled.div`
  margin-top: ${spaceL};
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  gap: ${spaceL};
`;

const ConfinedContentContainer = styled(ContentContainer)`
  @media ${mediaTopBarVisible} {
    display: none;
  }
`;
